import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getCaptainOrderUsers, getDeliveryUsers } from "../../../../../services/Admin/SalesScreenApi";
import { updateOrder } from "../../../../../services/Admin/OrdersApi";
import { useAuth } from "../../../../../Context/AuthContext";


const OrderDetailsSection = ({
  order,
  onEditOrder,
  onDeleteOrder,
  onOrderStatusUpdated,
  canDeleteOrder = false,
}) => {
  const [captainUsers, setCaptainUsers] = useState([]);
  const [deliveryUsers, setDeliveryUsers] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(order?.status || "");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const { canManageOrders } = useAuth();

  useEffect(() => {
    setCurrentStatus(order?.status || "");
  }, [order?.status]);

  // Fetch captain and delivery users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const captainResponse = await getCaptainOrderUsers();
        if (captainResponse.data) {
          setCaptainUsers(captainResponse.data);
        }

        const deliveryResponse = await getDeliveryUsers();
        if (deliveryResponse.data) {
          setDeliveryUsers(deliveryResponse.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  // Helper function to get captain name by id
  const getCaptainName = (captainId) => {
    if (!captainId) return "-";
    const captain = captainUsers.find(c => c.id === captainId);
    return captain ? captain.name : captainId;
  };

  // Helper function to get delivery user name by id
  const getDeliveryName = (deliveryId) => {
    if (!deliveryId) return "-";
    const delivery = deliveryUsers.find(d => d.id === deliveryId);
    return delivery ? delivery.name : deliveryId;
  };

  const getOrderSourceText = (type) => {
    switch (type) {
      case "local":
        return "كاشير";
      case "client":
        return "من خلال العميل";
      case "screen":
        return "من خلال الشاشة";
      default:
        return type || "-";
    }
  };

  // تحويل الوقت من صيغة 24 ساعة إلى 12 ساعة
  const formatTime12Hour = (timeString) => {
    if (!timeString) return "-";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${String(hour12).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const getOrderStatusText = (status) => {
    switch (status) {
      case "pending":
        return " قيد التجهيز";
      case "in_prepration":
      case "in_preparation":
        return "في التحضير";
      case "pickup":
        return "تم الاستلام من العميل";
      case "finished":
        return "منتهي";
      case "canceled":
      case "cancelled":
        return "ملغي";
      case "underway":
        return "قيد التوصيل";
      case "out_of_preparation":
        return "خارج التحضير";
      case "delivered":
        return "تم الاستلام من العميل";
      case "under_review":
        return "قيد المراجعه";
      default:
        return status || "-";
    }
  };

  const handleReviewAction = async (nextStatus) => {
    if (!order?.id || !nextStatus) return;

    setUpdatingStatus(true);
    try {
      await updateOrder({
        ...order,
        order_id: order.id,
        status: nextStatus,
        table_id: order.table_id ?? order?.table?.id ?? null,
        captain_order_id: order.captain_order_id ?? null,
        delivery_id: order.delivery_id ?? null,
        date: order.date,
        order_type: order.order_type || order.type,
      });

      setCurrentStatus(nextStatus);
      onOrderStatusUpdated?.(nextStatus);
    } catch (error) {
      console.error("Error updating order status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!order) return null;
  const currentOrderType = order.order_type || order.type;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          تفاصيل الطلب #{order.general_increment}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
        {canManageOrders() && (
          <IconButton
            size="small"
            onClick={onEditOrder}
            color="primary"
            title="تعديل بيانات الطلب"
            sx={{ color: "primary.main" }}
          >
            <EditIcon />
          </IconButton>
            )}
          {canDeleteOrder && (
          <IconButton
            size="small"
            onClick={onDeleteOrder}
            color="error"
            title="حذف الطلب"
            sx={{ color: "error.main" }}
          >
            <DeleteIcon />
          </IconButton>
            )}
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2">معلومات الطلب</Typography>
        <Typography variant="body2">النوع: {order.order_type || order.type}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ color: currentStatus === "under_review" ? "error.main" : "inherit", fontWeight: currentStatus === "under_review" ? 700 : 400 }}>
            الحالة: {getOrderStatusText(currentStatus)}
          </Typography>

          {currentStatus === "under_review" && (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={updatingStatus}
                onClick={() => handleReviewAction("pending")}
              >
                {updatingStatus ? <CircularProgress size={14} color="inherit" /> : "قبول وتحضير"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={updatingStatus}
                onClick={() => handleReviewAction("canceled")}
              >
                رفض
              </Button>
            </>
          )}
        </Box>
        <Typography variant="body2">الوقت المفتوح: {formatTime12Hour(order.open_time)}</Typography>
        <Typography variant="body2">التاريخ: {order.date}</Typography>
        {currentOrderType !== "delivery" && currentOrderType !== "takeaway" && (
          <Typography variant="body2">الكابتن: {getCaptainName(order.captain_order_id)}</Typography>
        )}
        {currentOrderType !== "tables" && (
          <Typography variant="body2">الديلفري: {getDeliveryName(order.delivery_id)}</Typography>
        )}
        <Typography variant="body2">طلب خارجي: {getOrderSourceText(order.type)}</Typography>

      </Box>

      <Divider sx={{ my: 1 }} />
    </Box>
  );
};

export default OrderDetailsSection;
